import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Button, IconButton, MenuItem, TextField,
  Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, CircularProgress, Tooltip
} from '@mui/material';
import { Paid, Cancel, Refresh } from '@mui/icons-material';
import escrowService from '../../services/escrowService';

const STATUS_COLORS = { pending: 'warning', processing: 'info', completed: 'success', cancelled: 'default' };

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * AdminRefunds
 * Review and process client escrow refund requests. Approving a request fires
 * the Stripe refund against the escrow's original payment and cancels the escrow.
 */
const AdminRefunds = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [approveDialog, setApproveDialog] = useState(null);
  const [denyDialog, setDenyDialog] = useState(null);
  const [denyReason, setDenyReason] = useState('');
  const [busy, setBusy] = useState(false);

  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await escrowService.getRefundRequests(statusFilter || 'all');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to load refund requests', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const submitApprove = async () => {
    setBusy(true);
    try {
      await escrowService.approveRefund(approveDialog.id);
      notify('Refund processed — funds returned to the client\'s payment method');
      setApproveDialog(null);
      load();
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to process refund', 'error');
    } finally { setBusy(false); }
  };

  const submitDeny = async () => {
    setBusy(true);
    try {
      await escrowService.denyRefund(denyDialog.id, denyReason);
      notify('Refund request denied');
      setDenyDialog(null);
      setDenyReason('');
      load();
    } catch (err) {
      notify(err.response?.data?.message || 'Failed to deny refund', 'error');
    } finally { setBusy(false); }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField select size="small" label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 150 }}>
          <MenuItem value="all">All</MenuItem>
          {['pending', 'processing', 'completed', 'cancelled'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <Button startIcon={<Refresh />} onClick={load}>Refresh</Button>
      </Box>

      <Card>
        <CardContent>
          {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Client</TableCell><TableCell>Project</TableCell>
                    <TableCell align="right">Refund Amount</TableCell><TableCell align="right">Escrow Total</TableCell>
                    <TableCell>Reason</TableCell><TableCell>Status</TableCell>
                    <TableCell>Requested</TableCell><TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>No refund requests found</TableCell></TableRow>
                  ) : rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell><Typography variant="caption">{r.client_email || r.client_user_id}</Typography></TableCell>
                      <TableCell>{r.project_title || r.project_id}</TableCell>
                      <TableCell align="right"><strong>{fmt(r.amount)}</strong></TableCell>
                      <TableCell align="right">{fmt(r.escrow_total)}</TableCell>
                      <TableCell sx={{ maxWidth: 240 }}>
                        <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>{r.processing_notes || '—'}</Typography>
                      </TableCell>
                      <TableCell><Chip size="small" label={r.status} color={STATUS_COLORS[r.status] || 'default'} /></TableCell>
                      <TableCell>{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</TableCell>
                      <TableCell align="right">
                        {r.status === 'pending' && (
                          <>
                            <Tooltip title="Approve & process refund">
                              <IconButton size="small" color="success" onClick={() => setApproveDialog(r)}><Paid fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Deny">
                              <IconButton size="small" color="error" onClick={() => setDenyDialog(r)}><Cancel fontSize="small" /></IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Approve dialog */}
      <Dialog open={!!approveDialog} onClose={() => !busy && setApproveDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Refund</DialogTitle>
        <DialogContent dividers>
          {approveDialog && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This immediately refunds {fmt(approveDialog.amount)} to the client's original payment
                method via Stripe and cancels the escrow. This cannot be undone.
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Project: {approveDialog.project_title || approveDialog.project_id}<br />
                Stripe reference: {approveDialog.funding_reference || '—'}
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialog(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="success" disabled={busy} onClick={submitApprove}
            startIcon={busy ? <CircularProgress size={16} /> : <Paid />}>
            {busy ? 'Processing...' : 'Approve & Refund'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deny dialog */}
      <Dialog open={!!denyDialog} onClose={() => !busy && setDenyDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Deny Refund Request</DialogTitle>
        <DialogContent dividers>
          <TextField fullWidth label="Reason (shared with the client)" multiline rows={3}
            value={denyReason} onChange={(e) => setDenyReason(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDenyDialog(null)} disabled={busy}>Cancel</Button>
          <Button variant="contained" color="error" disabled={busy} onClick={submitDeny}>Deny</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminRefunds;
